<template>
  <div :class="allSelectClasses">
    <div class="dropdown">
      <Popper
        v-model:open="open"
        :disabled="disabled"
        placement="bottom-end"
      >
        <template #trigger>
          <div class="select__trigger">
            <div
              v-if="!hideRate"
              :class="{ 'select__label-wrapper': true, 'typo-rating': ratingScale }"
            >
              <span
                v-if="selectIcon"
                class="select__label-icons"
              >
                <div
                  :class="{
                    'select__label-icon': true,
                    'select__label-icon--placeholder': showPlaceholder,
                  }"
                >
                  <UseSvg
                    :class="`icon icon--small ${currentIconClass}`"
                    :name="iconName"
                  />
                </div>
              </span>
              <span
                :class="{
                  'select__label select__label--flex': true,
                  'select__label--rating-scale': ratingScale,
                  'select__label--placeholder': showPlaceholder
                }"
              >
                {{ currentLabel }}
              </span>
            </div>

            <span
              v-if="!disabled"
              :class="{ select__arrow: true, 'select--margin-left': hideRate }"
            >
              <UseSvg
                class="icon icon--small"
                name="arrow_down_16"
              />
            </span>
          </div>
        </template>

        <DescriptionSelectDropdown
          v-if="open"
          :addText="addText"
          :iconName="iconName"
          :items="items"
          :label="label"
          :modelValue="modelValue"
          :name="name"
          :ratingScale="ratingScale"
          :removable="removable"
          :simple="simple"
          @cancel="cancel"
          @remove="remove"
          @update:modelValue="update"
        />
      </Popper>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import I18n from 'i18n-js';

import Popper from '@/components/generic/popper.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import DescriptionSelectDropdown from '@/components/form/description_select_dropdown.vue';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  placeholder: { type: String, default: '' },
  modelValue: { type: [String, Number], default: null },
  items: { type: Array, required: true },
  iconName: { type: String, default: null },
  selectClasses: { type: String, default: null },
  ratingScale: { type: Boolean, default: false },
  removable: { type: Boolean, default: true },
  hideRate: { type: Boolean, default: false },
  simple: { type: Boolean, default: false },
  selectIcon: { type: Boolean, default: false },
  indented: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  addText: { type: String, default: () => I18n.t('general.update') }
});
const emit = defineEmits(['update:modelValue', 'remove']);

const open = ref(false);

const currentLabel = computed(() => {
  let item = findItem(props.modelValue);

  if (item) return item['label'] || props.placeholder;
  return props.placeholder;
});

const currentIconClass = computed(() => {
  let item = findItem(props.modelValue);

  if (item) return item['iconClass'] || '';
  return '';
});

const showPlaceholder = computed(() => !findItem(props.modelValue));

const allSelectClasses = computed(() => {
  let classes =  {
    'select': true,
    'select--wrap-icon': props.selectIcon && !props.indented,
    'select--default-pointer': props.disabled,
  };
  if (props.selectClasses) {
    classes[props.selectClasses] = true;
  }
  return classes;
});

function remove() {
  open.value = false;
  emit('remove');
}

function cancel() {
  open.value = false;
}

function update(value) {
  open.value = false;
  emit('update:modelValue', value);
}

function findItem(value) {
  return props.items.find(i => i['value'] === value);
}
</script>

<style lang="scss">
@import '@/stylesheets/global';

$description-select-width: rem(350);
$description-select-simple-width: rem(430);
$description-select-info-height: (
  small: rem(200),
  medium: rem(124)
);
$description-select-indicator-size: rem(42);

.description-select {
  $block: &;

  &--simple {
    #{$block}__container {
      @include breakpoint(medium) {
        width: $description-select-simple-width;
      }
    }

    #{$block}__label {
      position: static;
    }

    #{$block}__values {
      background-color: map-get($background-color, dark);

      &--rating-scale {
        @include set-font-family('condensed-tabular-numbers');
      }
    }

    #{$block}__rating {
      position: relative;
      padding: bu(1) bu(1.25);

      + #{$block}__rating {
        margin-left: bu(.75);
      }

      #{$block}__label {
        font-weight: 500;
      }
    }

    input[type='radio']:checked + #{$block}__rating {
      background-color: map-get($background-color, lightest);
      border-radius: map-get($border-radius, heavy);
    }
  }

  &__container {
    border-bottom: bu(7) solid map-get($background-color, light);

    @include breakpoint(medium) {
      padding-top: bu(1);
      border-bottom: none;
      width: $description-select-width;
    }
  }

  input[type='radio'] {
    // the hidden inputs don't take up space
    position: absolute;
  }

  &__title {
    padding: bu(3) bu(2);

    div {
      border-left: rem(2) solid rgba(map-get($foreground-color, default), .3);
      padding-left: bu(2.5);
    }
  }

  &__values {
    display: flex;
    justify-content: space-around;
    padding: bu(1.25);
    border-radius: map-get($border-radius, heavy);
    background-color: map-get($background-color, default);
    margin: 0 bu(1);
    @include set-font-family('condensed-tabular-numbers');

    input {
      position: absolute;
    }

    &--disabled {
      pointer-events: none;
    }
  }

  &__rating {
    display: block;
    position: relative;
    cursor: pointer;

    input[type='radio']:checked + & {
      #{$block}__label {
        color: $input-bg-tint;
      }

      #{$block}__indicator {
        opacity: 1;
      }
    }

    input[type='radio']:disabled + & {
      #{$block}__label {
        color: map-get($background-color, lighter);
      }
    }
  }

  &__indicator {
    opacity: 0;
    width: $description-select-indicator-size;
    height: $description-select-indicator-size;
    @include transition(opacity);
  }

  &__label {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    color: map-get($foreground-color, dark);
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: bu(1.25) bu(1);
    order: -1;
    background-color: map-get($background-color, lightest);

    @include breakpoint(medium) {
      order: 1;
    }

    button {
      background-color: rgba(map-get($foreground-color, default), .11);
      padding: bu(1) bu(1.5);
    }

    &-end {
      display: flex;
      align-items: center;
      margin-left: auto;
    }
  }

  &__info {
    padding: bu(2) bu(3);
    color: map-get($foreground-color, default);
    min-height: map-get($description-select-info-height, small);
    line-height: rem(20);

    @include breakpoint(medium) {
      min-height: map-get($description-select-info-height, medium);
    }

    p {
      + p {
        margin-top: bu(2);
      }

      font-weight: normal;
    }
  }

  &__header {
    font-weight: 500;
  }

  &__list {
    list-style: disc;
    margin-top: bu(1);
    margin-left: bu(2);

    li {
      font-weight: normal;
    }
  }
}
</style>
